import type { RenderAdapter, PresentationProps } from './adapter';
import type { PresentationEvent } from '../postmessage/protocol';
import { PROTOCOL_VERSION, makeEnvelope, isValidEnvelope } from '../postmessage/envelope';
import { HOST_TO_IFRAME, IFRAME_TO_HOST } from '../postmessage/protocol';

const POSITIONS: Record<string, Record<string, string>> = {
  'bottom-right': { bottom: '0', right: '0' },
  'bottom-left': { bottom: '0', left: '0' },
  'top-right': { top: '0', right: '0' },
  'top-left': { top: '0', left: '0' },
};

// Collapsed size fits the pill-shaped Bubble (icon + label + send-affordance,
// see presentation/Bubble.tsx) plus room for its box-shadow — not a square,
// unlike the old circular-avatar bubble this replaced.
const COLLAPSED_W = '240px';
const COLLAPSED_H = '72px';
const EXPANDED_W = '420px';
const EXPANDED_H = '640px';

export function createIframeAdapter(opts: { baseUrl: string }): RenderAdapter {
  let iframe: HTMLIFrameElement | null = null;
  let lastProps: PresentationProps | null = null;
  let onEventHandler: ((e: PresentationEvent) => void) | null = null;
  const allowedOrigin = new URL(opts.baseUrl).origin;
  let pendingInit = false;

  function messageListener(ev: MessageEvent) {
    if (ev.origin !== allowedOrigin) return;
    if (!iframe || ev.source !== iframe.contentWindow) return;
    if (!isValidEnvelope(ev.data, 'mobeus-iframe')) return;

    const env = ev.data;
    if (env.type === IFRAME_TO_HOST.READY) {
      sendInit();
    } else if (env.type === IFRAME_TO_HOST.EVENT) {
      if (onEventHandler) onEventHandler(env.payload as PresentationEvent);
    } else if (env.type === IFRAME_TO_HOST.RESIZE) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      applySize(!!(env.payload as any)?.expanded);
    } else if (env.type === IFRAME_TO_HOST.ERROR) {
      console.error('[Mobeus iframe]', env.payload);
    }
  }

  function applySize(expanded: boolean) {
    if (!iframe) return;
    // Fullscreen layout (Phase 6) is gated in bootstrap.ts to require
    // avatar:true + render:"shadow", so `lastProps.layout === 'fullscreen'`
    // should never actually reach this adapter in practice — bootstrap
    // forces it back to `'panel'` before iframe mode's props are even
    // constructed. Handled defensively anyway, mirroring shadow.ts's
    // `applyHostLayout`, so a future relaxation of that gate doesn't leave
    // this adapter silently clipping a full-bleed avatar to a 420x640 box.
    const fullscreen = lastProps?.layout === 'fullscreen' && expanded;
    if (fullscreen) {
      iframe.style.top = '0';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.left = '0';
      iframe.style.width = '100vw';
      iframe.style.height = '100vh';
      return;
    }
    // Restore the configured corner position in case a prior fullscreen
    // state overwrote top/right/bottom/left above.
    const pos = POSITIONS[lastProps?.position ?? 'bottom-right'] ?? POSITIONS['bottom-right'];
    iframe.style.top = '';
    iframe.style.right = '';
    iframe.style.bottom = '';
    iframe.style.left = '';
    for (const [k, v] of Object.entries(pos)) {
      iframe.style.setProperty(k, v);
    }
    iframe.style.width = expanded ? EXPANDED_W : COLLAPSED_W;
    iframe.style.height = expanded ? EXPANDED_H : COLLAPSED_H;
  }

  function sendInit() {
    if (!iframe || !lastProps) { pendingInit = true; return; }
    pendingInit = false;
    const init = makeEnvelope('mobeus-host', HOST_TO_IFRAME.INIT, {
      protocolVersion: PROTOCOL_VERSION,
      position: lastProps.position,
      config: { toolCalls: lastProps.showToolCalls, citations: lastProps.showCitations },
    });
    iframe.contentWindow?.postMessage(init, allowedOrigin);
    const update = makeEnvelope('mobeus-host', HOST_TO_IFRAME.UPDATE, lastProps);
    iframe.contentWindow?.postMessage(update, allowedOrigin);
  }

  return {
    mount(props) {
      lastProps = props;
      iframe = document.createElement('iframe');
      iframe.setAttribute('data-mobeus-iframe', '');
      iframe.src = `${opts.baseUrl}/widget-presentation?v=${PROTOCOL_VERSION}`;
      iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-forms');
      const pos = POSITIONS[props.position] ?? POSITIONS['bottom-right'];
      const posCss = Object.entries(pos).map(([k, v]) => `${k}:${v}`).join(';');
      iframe.style.cssText = `position:fixed;z-index:2147483647;border:none;background:transparent;width:${COLLAPSED_W};height:${COLLAPSED_H};transition:width 0.3s ease,height 0.3s ease;${posCss}`;
      window.addEventListener('message', messageListener);
      document.body.appendChild(iframe);
    },
    update(partial) {
      if (!lastProps) return;
      lastProps = { ...lastProps, ...partial };
      if (!iframe) return;
      const env = makeEnvelope('mobeus-host', HOST_TO_IFRAME.UPDATE, lastProps);
      iframe.contentWindow?.postMessage(env, allowedOrigin);
      if (pendingInit) sendInit();
    },
    unmount() {
      window.removeEventListener('message', messageListener);
      if (iframe) iframe.remove();
      iframe = null;
      lastProps = null;
    },
    onEvent(handler) { onEventHandler = handler; },
  };
}
